import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Video, Palette, TrendingUp, Globe, Sparkles, Target, Users, Award, Youtube } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// YouTube Video Carousel Component
const YouTubeCarousel = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const rowRef = React.useRef(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await axios.get(`${API}/youtube/latest-videos`);

        if (res.data && res.data.video_ids) {
          setVideos(res.data.video_ids);
        } else {
          console.log("No videos returned", res.data);
        }
      } catch (err) {
        console.error("Video fetch failed:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const scrollLeft = () => {
    rowRef.current.scrollBy({ left: -400, behavior: "smooth" });
  };

  const scrollRight = () => {
    rowRef.current.scrollBy({ left: 400, behavior: "smooth" });
  };

  if (loading) {
    return <p className="text-center text-gray-400">Loading videos...</p>;
  }

  if (videos.length === 0) {
    return (
      <p className="text-center text-gray-400">
        No videos available right now.
      </p>
    );
  }

  return (
    <div className="relative">

      <button
        onClick={scrollLeft}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-black/60 text-white p-3 rounded-full"
      >
        ◀
      </button>

      <button
        onClick={scrollRight}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-black/60 text-white p-3 rounded-full"
      >
        ▶
      </button>

      <div
        ref={rowRef}
        className="flex gap-6 overflow-x-auto px-8 pb-6"
      >
       {videos.map((video, index) => {

  const videoId = video.video_id;

  return (
    <a
      key={index}
      href={`https://www.youtube.com/watch?v=${videoId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="group"
    >
      <motion.div
        whileHover={{ scale: 1.18 }}
        transition={{ duration: 0.25 }}
        className="relative w-[340px] flex-shrink-0 rounded-xl overflow-hidden bg-[#1e293b]"
      >

        {/* Thumbnail */}
        <div className="relative w-full pt-[56.25%]">
          <img
            src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
            alt={video.title}
            className="absolute top-0 left-0 w-full h-full object-cover"
          />

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
            <div className="w-14 h-14 bg-white text-black rounded-full flex items-center justify-center text-2xl">
              ▶
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="p-3">
          <p className="text-white text-sm font-medium">
            {video.title}
          </p>
        </div>

      </motion.div>
    </a>
  );
})}
</div>
    </div>
  );
};

const Home = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const services = [
    {
      icon: <Video className="w-8 h-8" />,
      title: 'Media Production',
      description: 'Video production, filming, editing, and multimedia storytelling.',
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: 'Digital Content Creation',
      description: 'Content for YouTube, social media, and digital platforms.',
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: 'Branding & Advertising',
      description: 'Brand identity creation, promotional campaigns, and advertising strategies.',
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Marketing & Digital Strategy',
      description: 'Helping businesses grow through targeted digital marketing.',
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: 'Online Platforms & Digital Projects',
      description: 'Building and managing digital media platforms and content ecosystems.',
    },
  ];

  const whyChooseUs = [
    { icon: <Sparkles className="w-6 h-6" />, text: 'Creative storytelling' },
    { icon: <Video className="w-6 h-6" />, text: 'Professional media production' },
    { icon: <Target className="w-6 h-6" />, text: 'Digital-first strategies' },
    { icon: <Users className="w-6 h-6" />, text: 'Client-focused solutions' },
  ];

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758843420234-e1b6fb0b4f5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGZsdWlkJTIwYmx1ZSUyMHB1cnBsZSUyMG5lb24lMjBncmFkaWVudCUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzczMDQ4MzIzfDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 hero-gradient"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.h1
            data-testid="hero-title"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl sm:text-6xl lg:text-8xl font-outfit font-bold tracking-tighter text-white mb-6 leading-tight"
          >
            TriVenza Private Limited
          </motion.h1>
          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg sm:text-xl lg:text-2xl text-gray-300 font-manrope mb-4 max-w-3xl mx-auto"
          >
            Creating Digital Experiences Through Media, Creativity, and Technology
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-sm sm:text-base text-gray-400 font-manrope mb-12 max-w-2xl mx-auto leading-relaxed"
          >
            TriVenza Private Limited is a modern media production and digital solutions company specializing in content creation, branding, marketing, and innovative digital media solutions.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to="/services"
              data-testid="hero-explore-services-btn"
              className="gradient-background text-white font-manrope font-semibold px-8 py-4 rounded-full hover:scale-105 transition-transform duration-200"
            >
              Explore Services
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-btn"
              className="bg-white/10 backdrop-blur-sm text-white font-manrope font-semibold px-8 py-4 rounded-full border border-white/20 hover:bg-white/20 transition-all duration-200"
            >
              Contact Us
            </Link>
          </motion.div>
        </div>
      </section>

      {/* About Preview Section */}
      <section data-testid="about-preview-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0f172a]">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-outfit font-bold tracking-tighter text-white mb-6">
              Who We Are
            </h2>
            <p className="text-base md:text-lg text-gray-400 font-manrope leading-relaxed mb-8 max-w-4xl">
              TriVenza Private Limited is a media production and digital services company engaged in diverse business activities including digital content creation, media production, online platforms, advertising, branding, marketing, and other technology-driven services.
            </p>
            <Link
              to="/about"
              data-testid="about-learn-more-btn"
              className="inline-block text-[#7c3aed] font-manrope font-semibold hover:text-[#3b82f6] transition-colors duration-200"
            >
              Learn More →
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            {...fadeInUp}
            className="text-4xl md:text-5xl font-outfit font-bold tracking-tighter text-white mb-16 text-center"
          >
            What We Do
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                {...fadeInUp}
                transition={{ delay: index * 0.1 }}
                data-testid={`service-card-${index}`}
                className="bg-[#0f172a]/50 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:border-[#7c3aed]/50 hover:shadow-lg hover:shadow-[#7c3aed]/20 transition-all duration-300 glow-card"
              >
                <div className="text-[#7c3aed] mb-4">{service.icon}</div>
                <h3 className="text-xl font-outfit font-semibold text-white mb-3">{service.title}</h3>
                <p className="text-gray-400 font-manrope text-sm leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Media/Portfolio Section */}
      <section data-testid="media-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0f172a]">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            {...fadeInUp}
            className="text-4xl md:text-5xl font-outfit font-bold tracking-tighter text-white mb-6 text-center"
          >
            Our Media Portfolio
          </motion.h2>
          <motion.p
            {...fadeInUp}
            className="text-center text-gray-400 font-manrope mb-16 max-w-3xl mx-auto"
          >
            Explore our creative content on YouTube - Babyland Rhymes, where we create engaging children's content and nursery rhymes.
          </motion.p>
          
          {/* YouTube Video Carousel */}
          <YouTubeCarousel />

          {/* Channel Link */}
          <motion.div {...fadeInUp} className="text-center mt-12">
            <a
              href="https://www.youtube.com/@babylandrhymes"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-channel-btn"
              className="inline-flex items-center gap-2 gradient-background text-white font-manrope font-semibold px-8 py-4 rounded-full hover:scale-105 transition-transform duration-200"
            >
              <Youtube className="w-5 h-5" />
              Visit Our YouTube Channel
            </a>
          </motion.div>

          {/* Stats/Info Cards */}
          <motion.div {...fadeInUp} className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
            <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] border border-white/10 rounded-2xl p-6 text-center">
              <div className="text-[#7c3aed] mb-2">
                <Video className="w-8 h-8 mx-auto" />
              </div>
              <h3 className="text-white font-outfit font-bold text-xl mb-1">Children's Content</h3>
              <p className="text-gray-400 font-manrope text-sm">Engaging nursery rhymes & educational videos</p>
            </div>
            <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] border border-white/10 rounded-2xl p-6 text-center">
              <div className="text-[#3b82f6] mb-2">
                <Sparkles className="w-8 h-8 mx-auto" />
              </div>
              <h3 className="text-white font-outfit font-bold text-xl mb-1">Creative Production</h3>
              <p className="text-gray-400 font-manrope text-sm">High-quality animated content for kids</p>
            </div>
            <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] border border-white/10 rounded-2xl p-6 text-center">
              <div className="text-[#7c3aed] mb-2">
                <Users className="w-8 h-8 mx-auto" />
              </div>
              <h3 className="text-white font-outfit font-bold text-xl mb-1">Growing Community</h3>
              <p className="text-gray-400 font-manrope text-sm">Building a community of young learners</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section data-testid="why-choose-us-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            {...fadeInUp}
            className="text-4xl md:text-5xl font-outfit font-bold tracking-tighter text-white mb-16 text-center"
          >
            Why Work With TriVenza
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                {...fadeInUp}
                transition={{ delay: index * 0.1 }}
                data-testid={`why-choose-card-${index}`}
                className="flex flex-col items-center text-center"
              >
                <div className="bg-gradient-to-br from-[#7c3aed] to-[#3b82f6] p-4 rounded-2xl mb-4">
                  <div className="text-white">{item.icon}</div>
                </div>
                <p className="text-white font-manrope font-medium">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-32 px-6 md:px-12 bg-gradient-to-br from-[#7c3aed] to-[#3b82f6]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            {...fadeInUp}
            className="text-4xl md:text-6xl font-outfit font-bold tracking-tighter text-white mb-8"
          >
            Let's Create Something Powerful Together
          </motion.h2>
          <motion.div {...fadeInUp}>
            <Link
              to="/contact"
              data-testid="cta-start-project-btn"
              className="inline-block bg-white text-[#7c3aed] font-manrope font-bold px-10 py-5 rounded-full hover:scale-105 transition-transform duration-200 text-lg"
            >
              Start a Project
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
